import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, MessageSquare, Users, BarChart, Palette, Zap, Shield, HeadphonesIcon, ArrowRight, Copy, Check, Loader2, Building, Rocket, TrendingUp, Link } from "lucide-react";

const SUPABASE_URL = "https://ydwqhnwvmsghmfdbgwub.supabase.co";

// Stripe Price IDs
const STRIPE_PRICES = {
  basic: "price_1RTyVIE6AAfMiC13F6cqxD6A",
  pro: "price_1RTyWJE6AAfMiC13dkY7KJom",
  premium: "price_1RTyX3E6AAfMiC13CBWF8vVu"
};

// Price tiers - conversion-optimiert
const PRICING_TIERS = [
  {
    name: "Free",
    price: "0",
    period: "für immer",
    description: "Perfekt zum Ausprobieren",
    features: [
      "Bis zu 10 Nachrichten pro Tag",
      "Intelligente Basis-Antworten auf häufige Fragen",
      "Standard-Design",
      '"Powered by Klartech" Branding',
    ],
    limits: { daily: 10 },
    highlighted: false,
    cta: "Kostenlos testen"
  },
  {
    name: "Basic",
    price: "12",
    period: "pro Monat",
    description: "Für Privatpersonen & kleine Webseiten",
    features: [
      "Bis zu 150 Nachrichten pro Monat",
      "Rückruf-Funktion (Leads direkt zu Ihnen)",
      "Name & Kontaktdaten der Anfragenden",
      "E-Mail-Support durch Klartech",
      '"Powered by Klartech" Branding',
    ],
    limits: { monthly: 150 },
    highlighted: false,
    cta: "Basic sichern"
  },
  {
    name: "Pro",
    price: "39",
    period: "pro Monat",
    description: "Für kleinere Unternehmen, die verfügbar sein müssen",
    features: [
      "Bis zu 750 Nachrichten pro Monat",
      "Eigenes Logo, Farben und Texte",
      "Eigene Wissensdatenbank (z.B. Leistungen, Preise, FAQ)",
      "Monatlicher Kurz-Report mit den wichtigsten Fragen",
      "Kein Klartech Branding",
      "Prioritäts-Support",
    ],
    limits: { monthly: 750 },
    highlighted: true,
    cta: "Pro starten",
    badge: "Beliebt"
  },
  {
    name: "Premium",
    price: "89",
    period: "pro Monat",
    description: "Für anspruchsvolle KMU & Agenturen",
    features: [
      "Bis zu 2.000 Nachrichten pro Monat",
      "White-Label-Lösung (vollständig als Ihr Unternehmen)",
      "Vollständige Anpassung von Antworten, Design und Widgets",
      "API-Zugang für Automatisierungen",
      "Dedizierter persönlicher Ansprechpartner",
      "Optional im Bundle mit Klartech IT-Retainer",
    ],
    limits: { monthly: 2000 },
    highlighted: false,
    cta: "Premium anfragen"
  }
];

// Warum Klartech Chatbot - 5 Features
const WHY_FEATURES = [
  {
    icon: Building,
    title: "Speziell für kleine Unternehmen & Selbstständige",
    description: "Keine überladene Enterprise-Lösung, sondern ein klar strukturierter Bot mit verständlichen Einstellungen, fairen Preisen und monatlicher Kündbarkeit."
  },
  {
    icon: Shield,
    title: "Deutsch, DSGVO-konform & lokal betreut",
    description: "Datenverarbeitung in Deutschland, transparente Datenschutzerklärung und ein persönlicher Ansprechpartner aus der Region statt anonymer Großkonzern."
  },
  {
    icon: Rocket,
    title: "In Minuten startklar – ohne Technikstress",
    description: "Sie erhalten einen einfachen Einbettungs-Code. Einfügen, fertig. Design und Antworten passen Sie direkt im Browser an, ganz ohne Programmierkenntnisse."
  },
  {
    icon: TrendingUp,
    title: "Direkt auf Umsatz und Entlastung optimiert",
    description: "Der Bot beantwortet Standardfragen, sammelt Kontaktdaten, erzeugt Rückruf-Wünsche und filtert Anfragen vor – so bleibt mehr Zeit für die wirklich wichtigen Fälle."
  },
  {
    icon: Link,
    title: "Nahtlos mit Klartech-IT-Service kombinierbar",
    description: "Auf Wunsch integrieren wir den Chat in Ihre bestehenden Klartech-Retainer. Ein Ansprechpartner für IT-Betreuung, Website und KI-Assistent."
  }
];

const USE_CASES = [
  {
    title: "Arztpraxen & Therapeuten",
    description: "Automatische Beantwortung von Fragen zu Öffnungszeiten, Leistungen und Terminvereinbarung.",
    icon: HeadphonesIcon
  },
  {
    title: "Handwerksbetriebe",
    description: "24/7 Anfragen zu Services, Preisen und Verfügbarkeit beantworten.",
    icon: Zap
  },
  {
    title: "Kanzleien & Berater",
    description: "Erste Einschätzungen geben und qualifizierte Leads generieren.",
    icon: Shield
  },
  {
    title: "Online-Shops",
    description: "Produktberatung, FAQ und Kaufunterstützung automatisieren.",
    icon: BarChart
  }
];

export default function ChatbotProduct() {
  const [email, setEmail] = useState("");
  const [contactName, setContactName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationResult, setRegistrationResult] = useState<{ apiKey: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState<string | null>(null);
  const { toast } = useToast();

  const handleUpgrade = async (plan: "basic" | "pro" | "premium") => {
    if (!registrationResult?.apiKey) {
      toast({
        title: "Bitte zuerst registrieren",
        description: "Erstellen Sie zunächst einen kostenlosen Account.",
        variant: "destructive"
      });
      return;
    }

    setIsUpgrading(plan);
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/chatbot-create-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          apiKey: registrationResult.apiKey, 
          priceId: STRIPE_PRICES[plan] 
        })
      });

      const data = await response.json();

      if (response.ok && data.url) {
        window.open(data.url, "_blank");
      } else {
        throw new Error(data.error || "Checkout konnte nicht erstellt werden");
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
    } finally {
      setIsUpgrading(null);
    }
  };

  const handleRegister = async () => {
    if (!email || !contactName) {
      toast({
        title: "Fehlende Angaben",
        description: "Bitte geben Sie E-Mail und Namen ein.",
        variant: "destructive"
      });
      return;
    }

    setIsRegistering(true);
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/chatbot-register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, contactName, companyName })
      });

      const data = await response.json();

      if (response.ok) {
        setRegistrationResult({ apiKey: data.apiKey });
        toast({
          title: "Erfolgreich registriert!",
          description: "Ihr API-Key wurde erstellt.",
        });
      } else if (response.status === 409) {
        toast({
          title: "E-Mail bereits registriert",
          description: "Diese E-Mail ist bereits registriert. Ihr API-Key wurde angezeigt.",
        });
        setRegistrationResult({ apiKey: data.apiKey });
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      toast({
        title: "Fehler bei der Registrierung",
        description: error instanceof Error ? error.message : "Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
    } finally {
      setIsRegistering(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const embedCode = registrationResult ? 
    `<script src="https://klartech-mueritz.de/chatbot-widget.js" data-api-key="${registrationResult.apiKey}"></script>` : 
    "";

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-foreground mb-6">
              Ihr KI-Assistent, der{" "}
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                nie Feierabend macht.
              </span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Beantworten Sie Kundenfragen automatisch – rund um die Uhr, ohne zusätzliches Personal.
            </p>
            
            {/* Bullet Points */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center mb-8">
              <div className="flex items-center gap-2 text-foreground">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>Mehr Anfragen in echte Aufträge verwandeln</span>
              </div>
              <div className="flex items-center gap-2 text-foreground">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>Standardfragen automatisieren und Zeit sparen</span>
              </div>
              <div className="flex items-center gap-2 text-foreground">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>In wenigen Minuten auf Ihrer Website eingebunden</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" className="gap-2" asChild>
                <a href="#register">
                  Jetzt kostenlos testen <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="#pricing">Preise ansehen</a>
              </Button>
            </div>
          </div>

          {/* Demo preview */}
          <div className="relative max-w-md mx-auto">
            <div className="bg-card rounded-2xl shadow-2xl border overflow-hidden">
              <div className="bg-primary p-4 text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold">Ihr KI-Assistent</div>
                  <div className="text-sm opacity-90">Online</div>
                </div>
              </div>
              <div className="p-4 space-y-3 bg-muted/50">
                <div className="bg-card rounded-lg p-3 shadow-sm max-w-[80%]">
                  Hallo! Wie kann ich Ihnen heute helfen?
                </div>
                <div className="bg-primary text-white rounded-lg p-3 max-w-[80%] ml-auto">
                  Was sind Ihre Öffnungszeiten?
                </div>
                <div className="bg-card rounded-lg p-3 shadow-sm max-w-[80%]">
                  Wir sind Mo-Fr von 9-18 Uhr für Sie da. Möchten Sie einen Termin vereinbaren?
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Warum Klartech Chatbot - 5 Features */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-4">Warum Klartech Chatbot?</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Speziell für kleine Unternehmen & Selbstständige entwickelt.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_FEATURES.map((feature, i) => (
              <Card key={i} className={i === 4 ? "md:col-span-2 lg:col-span-1" : ""}>
                <CardContent className="pt-6">
                  <feature.icon className="w-10 h-10 mb-4 text-primary" />
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-4 bg-muted/50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-4">Perfekt für viele Branchen</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Unser KI-Chatbot passt sich an Ihre Branche und Ihr Unternehmen an.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {USE_CASES.map((useCase, i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <useCase.icon className="w-10 h-10 mb-4 text-primary" />
                  <h3 className="font-semibold mb-2">{useCase.title}</h3>
                  <p className="text-sm text-muted-foreground">{useCase.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-4">Ihr eigener KI-Assistent – in Minuten startklar.</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Starten Sie kostenlos und erweitern Sie nur, wenn Sie wirklich mehr brauchen. Keine Einrichtungskosten, keine Mindestlaufzeit.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRICING_TIERS.map((tier, i) => (
              <Card 
                key={i} 
                className={`relative ${tier.highlighted ? "border-primary border-2 shadow-lg" : ""}`}
              >
                {tier.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                    {tier.badge}
                  </span>
                )}
                <CardHeader>
                  <CardTitle>{tier.name}</CardTitle>
                  <CardDescription>{tier.description}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">{tier.price}€</span>
                    <span className="text-muted-foreground"> / {tier.period}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {tier.name === "Free" ? (
                    <Button 
                      className="w-full" 
                      variant="outline"
                      asChild
                    >
                      <a href="#register">{tier.cta}</a>
                    </Button>
                  ) : (
                    <Button 
                      className="w-full" 
                      variant={tier.highlighted ? "default" : "outline"}
                      onClick={() => handleUpgrade(tier.name.toLowerCase() as "basic" | "pro" | "premium")}
                      disabled={isUpgrading !== null || !registrationResult}
                    >
                      {isUpgrading === tier.name.toLowerCase() ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : null}
                      {registrationResult ? tier.cta : "Erst registrieren"}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Trust Block */}
          <div className="mt-8 text-center text-muted-foreground text-sm space-y-1">
            <p>✓ Alle Pläne können monatlich gekündigt werden.</p>
            <p>✓ Upgrades sind jederzeit mit einem Klick möglich.</p>
          </div>
        </div>
      </section>

      {/* Registration */}
      <section id="register" className="py-20 px-4 bg-muted/50">
        <div className="container mx-auto max-w-xl">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Jetzt kostenlos starten</CardTitle>
              <CardDescription>
                Registrieren Sie sich in unter 60 Sekunden und erhalten Sie sofort Ihren API-Key und Einbettungs-Code.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!registrationResult ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Name *</label>
                    <Input 
                      placeholder="Max Mustermann" 
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">E-Mail *</label>
                    <Input 
                      type="email" 
                      placeholder="max@beispiel.de" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Unternehmen (optional)</label>
                    <Input 
                      placeholder="Musterfirma GmbH" 
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                    />
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={handleRegister}
                    disabled={isRegistering}
                  >
                    {isRegistering ? "Wird registriert..." : "Kostenlos registrieren"}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    Mit der Registrierung akzeptieren Sie unsere{" "}
                    <a href="/datenschutz" className="underline">Datenschutzerklärung</a>. 
                    Ihre Daten werden in Deutschland verarbeitet (DSGVO-konform).
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="text-center">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Erfolgreich registriert!</h3>
                    <p className="text-muted-foreground">
                      Kopieren Sie den Code unten und fügen Sie ihn vor dem schließenden &lt;/body&gt; Tag ein.
                    </p>
                  </div>
                  
                  <div className="relative">
                    <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                      <code>{embedCode}</code>
                    </pre>
                    <Button 
                      size="sm" 
                      variant="secondary"
                      className="absolute top-2 right-2 gap-1"
                      onClick={() => copyToClipboard(embedCode)}
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied ? "Kopiert!" : "Kopieren"}
                    </Button>
                  </div>

                  <div className="bg-primary/10 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">Ihr API-Key:</h4>
                    <code className="text-sm bg-background px-2 py-1 rounded">
                      {registrationResult.apiKey}
                    </code>
                    <p className="text-sm text-muted-foreground mt-2">
                      Bewahren Sie diesen Key sicher auf. Sie benötigen ihn für Einstellungen und Upgrades.
                    </p>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button variant="outline" className="w-full" asChild>
                      <a href="/chatbot-admin">Zur Bot-Konfiguration →</a>
                    </Button>
                    <Button variant="ghost" className="w-full" asChild>
                      <a href="/chatbot-api-docs">API-Dokumentation ansehen</a>
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="py-20 px-4 bg-muted/50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-4">Verbinden Sie Ihren Chatbot mit Ihren Tools</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Über Webhooks leitet Ihr Chatbot Events automatisch an Ihre bestehenden Systeme weiter – ohne Programmierung.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚡</span>
                </div>
                <h3 className="font-semibold mb-2">1. Webhook einrichten</h3>
                <p className="text-sm text-muted-foreground">Tragen Sie die URL Ihres Zielsystems ein (Zapier, Make.com, n8n oder Ihre API).</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🔔</span>
                </div>
                <h3 className="font-semibold mb-2">2. Events auswählen</h3>
                <p className="text-sm text-muted-foreground">Wählen Sie, bei welchen Ereignissen Ihr System benachrichtigt wird: Neuer Lead, Rückruf, Chat-Start.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🚀</span>
                </div>
                <h3 className="font-semibold mb-2">3. Automatisch verbunden</h3>
                <p className="text-sm text-muted-foreground">Ab sofort werden Events in Echtzeit an Ihr CRM, Buchungstool oder Ticketsystem weitergeleitet.</p>
              </CardContent>
            </Card>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            {['Zapier', 'Make.com', 'n8n', 'Google Calendar', 'HubSpot', 'Eigene API'].map((tool) => (
              <Badge key={tool} variant="outline" className="px-4 py-2 text-sm">
                {tool}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-12">Häufige Fragen</h2>
          <div className="space-y-6">
            {[
              {
                q: "Wie lange dauert die Einrichtung?",
                a: "In der Regel weniger als 5 Minuten. Sie fügen den bereitgestellten Code einfach auf Ihrer Website ein und der Bot ist sofort einsatzbereit."
              },
              {
                q: "Kann ich das Design anpassen?",
                a: "Ja. Im Pro- und Premium-Plan passen Sie Farben, Logo und Texte komplett an Ihre Marke an – ohne Programmierkenntnisse."
              },
              {
                q: "Wie intelligent ist der Bot?",
                a: "Der Assistent nutzt moderne KI-Modelle (z.B. GPT/Gemini) und kann natürliche Gespräche führen. Im Pro- und Premium-Plan lernt er zusätzlich aus Ihrer eigenen Wissensdatenbank."
              },
              {
                q: "Was passiert, wenn das Nachrichten-Limit erreicht ist?",
                a: "Der Bot informiert Besucher freundlich, dass das Limit erreicht wurde, und bietet alternative Kontaktwege an (z.B. Formular, Telefon). Sie können jederzeit mit wenigen Klicks in einen höheren Plan wechseln."
              },
              {
                q: "Ist der Service DSGVO-konform?",
                a: "Ja. Die Datenverarbeitung erfolgt in Deutschland. Personenbezogene Daten werden nur mit Einwilligung gespeichert und können jederzeit gelöscht werden."
              },
              {
                q: "Wie funktionieren die Webhook-Integrationen?",
                a: "Ab dem Basic-Plan können Sie Webhook-URLs hinterlegen. Bei Ereignissen wie neuen Leads oder Rückruf-Anfragen sendet der Bot automatisch die Daten an Ihr System (z.B. Zapier, Make.com, CRM). Die Payloads werden optional mit HMAC-SHA256 signiert."
              }
            ].map((faq, i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-2">{faq.q}</h3>
                  <p className="text-muted-foreground">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 px-4 bg-primary text-primary-foreground">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-4">Bereit für Ihren KI-Assistenten?</h2>
          <p className="text-lg opacity-90 mb-8">
            Starten Sie kostenlos – und upgraden Sie erst, wenn Sie merken, wie viel Support der Bot Ihnen abnimmt.
          </p>
          <Button size="lg" variant="secondary" asChild>
            <a href="#register">Jetzt kostenlos testen</a>
          </Button>
        </div>
      </section>
    </div>
  );
}
